import {
  isBOL,
  isDrop,
  isOrderChange,
  isOrderEvent,
  useOrderHistory,
} from '@/api/hooks/useOrderHistory'
import type { OrderPerspectiveView } from '@/api/hooks/useOrders'
import { CloseOutlined } from '@ant-design/icons'
import { Vertical } from '@gravitate-js/excalibrr'
import { Drawer, Empty, Spin, theme } from 'antd'
import { BOLCard } from './BOLCard'
import { DropCard } from './DropCard'
import { OrderChangeCard } from './OrderChangeCard'
import { OrderEventCard } from './OrderEventCard'

interface OrderHistoryDrawerProps {
  order: OrderPerspectiveView | null
  open: boolean
  onClose: () => void
}

export function OrderHistoryDrawer({
  order,
  open,
  onClose,
}: OrderHistoryDrawerProps) {
  const { token } = theme.useToken()
  const orderNumber =
    order?.internal_order_number ?? order?.partner_order_number
  const { data: items, isLoading } = useOrderHistory(orderNumber ?? null)

  // Sort by created_on descending (newest first) - already sorted from backend but ensure
  const sortedItems = [...(items ?? [])].sort(
    (a, b) =>
      new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
  )

  return (
    <Drawer
      title={`Order ${orderNumber ?? ''} History`}
      placement='right'
      width={680}
      open={open}
      onClose={onClose}
      closeIcon={<CloseOutlined />}
      styles={{
        body: { padding: 16 },
      }}
    >
      {isLoading ? (
        <Vertical
          className='h-full'
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <Spin />
          <span style={{ marginTop: 12, color: token.colorTextSecondary }}>
            Loading history...
          </span>
        </Vertical>
      ) : sortedItems.length === 0 ? (
        <Empty description='No history recorded for this order' />
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Continuous timeline track */}
          {sortedItems.length > 1 && (
            <div
              style={{
                position: 'absolute',
                left: 5, // center of 12px dot
                top: 24, // align with first dot (paddingTop 18 + half dot 6)
                bottom: 24, // align with last dot
                width: 2,
                backgroundColor: token.colorBorderSecondary,
                zIndex: 0,
              }}
            />
          )}
          <Vertical gap={12}>
            {sortedItems.map((item) => {
              if (isOrderChange(item)) {
                return <OrderChangeCard key={item.id} change={item} />
              }
              if (isBOL(item)) {
                return <BOLCard key={item.id} bol={item} />
              }
              if (isDrop(item)) {
                return <DropCard key={item.id} drop={item} />
              }
              if (isOrderEvent(item)) {
                return <OrderEventCard key={item.id} event={item} />
              }
              return null
            })}
          </Vertical>
        </div>
      )}
    </Drawer>
  )
}
